/**
 * Server-Timing parsing for the order-latency probe (speed-demon 260904, T-A3).
 *
 * The relay's order routes emit a `Server-Timing` header of cumulative
 * per-stage offsets (relay/src/services/orderTiming.ts — `recv`, `signed`,
 * `venue_sent`, `venue_acked`, `responded`, …; ms floats since relay receipt).
 * The browser probe attaches the parsed stages to its in-flight sample so
 * `window.__orderLatency()` can split a slow order into client-side pre-send,
 * network, relay-internal, and venue round-trip time.
 */

/**
 * Parse a Server-Timing header (`name;dur=1.2, name2;dur=3`) into a
 * `name → dur(ms)` map. Malformed entries (no name, no parseable `dur`) are
 * ignored. Returns null for a null/absent header or one with no usable entry.
 */
export function parseServerTiming(header: string | null): Record<string, number> | null {
  if (header == null) return null;
  const out: Record<string, number> = {};
  for (const entry of header.split(',')) {
    const parts = entry.trim().split(';');
    const name = parts[0]?.trim();
    // RFC 7230 token — a bare comma / empty segment yields no name; skip.
    if (!name || !/^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/.test(name)) continue;
    for (const param of parts.slice(1)) {
      const eq = param.indexOf('=');
      if (eq === -1) continue;
      if (param.slice(0, eq).trim().toLowerCase() !== 'dur') continue;
      const dur = parseFloat(param.slice(eq + 1).trim());
      if (Number.isFinite(dur)) out[name] = dur;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Split the relay's cumulative stages into venue round-trip vs relay-internal
 * time:
 *
 *   venueMs = venue_acked − venue_sent        (null when either stage absent)
 *   relayMs = responded − (venue_acked − venue_sent)
 *             (null when `responded` absent; when the venue stages are absent
 *              the whole `responded` span is attributed to the relay)
 */
export function relayVenueSplit(
  stages: Record<string, number> | null,
): { relayMs: number | null; venueMs: number | null } {
  if (!stages) return { relayMs: null, venueMs: null };
  const venueMs =
    typeof stages.venue_acked === 'number' && typeof stages.venue_sent === 'number'
      ? stages.venue_acked - stages.venue_sent
      : null;
  const relayMs =
    typeof stages.responded === 'number' ? stages.responded - (venueMs ?? 0) : null;
  return { relayMs, venueMs };
}
